import { useState, useEffect, useRef, memo } from "react";
import { Texture, Container } from "pixi.js";
import React from "react";
import gsap from "gsap";
import { TileTitle } from "./TileTitle";
import { GhostTile } from "./GhostTile";
import { TileContainer } from "./TileWrapper";
import { TileCursor } from "./TileCursor";
import { TileLoadingState, TileProps, TitleColor } from "./Tile.types";
import { useTextureCache } from "../../hooks/useTextureCache";
import TileErrorBoundary from "./TileErrorBoundary";

export const Tile = memo(function Tile({
  index,
  x,
  y = 0,
  imageUrl,
  title,
  titleFontSize = 18,
  titleColor = TitleColor.DEFAULT,
  borderThickness = 2,
  borderColor = 0xffffff,
  isFocused,
  borderGap = 6,
  cornerRadius = 10,
  width = 180,
  height = 100,
  tileLoadingState = TileLoadingState.INITIAL,
  filters,
  skipImageLoad = false,
}: TileProps): React.ReactElement | null {
  // Get texture cache hooks first, before any state that might use them
  const { loadTexture, loadRoundedTexture, getTexture } = useTextureCache();

  const [texture, setTexture] = useState<Texture | null>(() => {
    if (
      imageUrl &&
      tileLoadingState !== TileLoadingState.ERROR &&
      !skipImageLoad
    ) {
      // Check for rounded texture if cornerRadius > 0
      const cacheKey =
        cornerRadius > 0 ? `${imageUrl}?rounded=${cornerRadius}` : imageUrl;
      return getTexture(cacheKey);
    }
    return null;
  });
  const [status, setStatus] = useState<TileLoadingState>(
    () => tileLoadingState || TileLoadingState.LOADING
  );

  const ghostRef = useRef<Container | null>(null);
  const spriteRef = useRef<Container | null>(null);

  // Effect to load the texture
  useEffect(() => {
    let mounted = true;

    const load = async () => {
      if (skipImageLoad) {
        return;
      }
      try {
        // Try to get from cache first

        if (texture) {
          if (mounted) {
            setStatus(TileLoadingState.LOADED);
          }
          return;
        }

        // If not in cache, start loading
        const tex =
          cornerRadius > 0
            ? await loadRoundedTexture(imageUrl, cornerRadius)
            : await loadTexture(imageUrl);

        if (mounted) {
          setTexture(tex);
          setStatus(TileLoadingState.LOADED);
        }
      } catch {
        if (mounted) {
          setTexture(null);
          setStatus(TileLoadingState.ERROR);
        }
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, [
    imageUrl,
    getTexture,
    loadTexture,
    loadRoundedTexture,
    tileLoadingState,
    texture,
    cornerRadius,
    width,
  // width & height removed from dependency list for rounded key (server-sized image)
  ]);

  // Effect to handle visual transitions (fade in/out)
  useEffect(() => {
    const ghost = ghostRef.current;
    const sprite = spriteRef.current;

    if (!ghost || !sprite) return;

    try {
      // Kill any ongoing animations on these elements to prevent conflicts
      gsap.killTweensOf([ghost, sprite]);

      const showGhost = status === "loading" || status === "error";

      gsap.to(ghost, {
        alpha: showGhost ? 1 : 0,
        duration: 0.3,
        ease: "power2.out",
      });

      gsap.to(sprite, {
        alpha: showGhost ? 0 : 1,
        duration: 0.3,
        ease: "power2.out",
      });
    } catch (error) {
      console.warn("GSAP animation error in Tile component:", error);
      // Fallback: set alpha directly if GSAP fails
      if (ghost)
        ghost.alpha = status === "loading" || status === "error" ? 1 : 0;
      if (sprite)
        sprite.alpha = status === "loading" || status === "error" ? 0 : 1;
    }

    return () => {
      try {
        gsap.killTweensOf([ghost, sprite]);
      } catch (error) {
        console.warn("Error cleaning up GSAP animations:", error);
      }
    };
  }, [status]);

  const ghostFillColor = status === "error" ? 0x550000 : 0x333333; // Dark red for error

  return (
    <TileErrorBoundary
      onError={(error, errorInfo) => {
        console.error(`Tile[${index}] error:`, error, errorInfo);
      }}
    >
      <TileContainer
        key={`tile-${index}`}
        x={x}
        y={y}
        label={`Tile[${index}] - ${title || "Untitled"}`}
      >
        {/* Border (with gap) */}
        {isFocused ? (
          <TileCursor
            tileImageConfig={{
              width,
              height,
              cornerRadius,
              fill: 0xffffff,
            }}
            borderGap={borderGap}
            borderThickness={borderThickness}
            borderColor={isFocused ? "yellow" : borderColor}
          />
        ) : null}

        {/* Real Sprite - shown when loaded */}
        <pixiContainer
          ref={spriteRef}
          label={`Sprite[${index}]`}
          cullable={true}
        >
          {(() => {
            try {
              return (
                status === "loaded" &&
                texture && (
                  <pixiSprite
                    texture={texture}
                    width={width}
                    height={height}
                    filters={filters}
                  />
                )
              );
            } catch (error) {
              console.warn(`Error rendering sprite for Tile[${index}]:`, error);
              return null;
            }
          })()}
        </pixiContainer>

        {/* Ghost Tile - for loading and error states */}
        <pixiContainer ref={ghostRef} label={`Ghost[${index}]`}>
          <GhostTile
            tileImageConfig={{
              width,
              height,
              cornerRadius,
              fill: ghostFillColor,
            }}
          />
        </pixiContainer>

        {/* Tile Title */}
        {title && (
          <TileTitle
            text={title}
            fontSize={titleFontSize}
            color={titleColor}
            position={{ x: 0, y: height + 10 }}
            maxWidth={width - 10} // Account for 10px padding on each side
          />
        )}
      </TileContainer>
    </TileErrorBoundary>
  );
});
